import Dexie, { Table } from 'dexie';
import type { Budget, Transaction, SyncConflict } from '@/types';

/**
 * IndexedDB database for offline storage.
 * Uses Dexie.js for a clean, promise-based API.
 */
export class BalanceDatabase extends Dexie {
  budgets!: Table<Budget, string>;
  transactions!: Table<Transaction, string>;
  syncQueue!: Table<QueuedChange, number>;
  conflicts!: Table<StoredConflict, number>;
  metadata!: Table<Metadata, string>;
  deviceUnlock!: Table<DeviceUnlockRecord, string>;

  constructor() {
    super('BalanceDB');
    
    this.version(1).stores({
      budgets: 'id, ownerId, parentId, startDate, endDate, lastModified, [ownerId+startDate]',
      transactions: 'id, budgetId, date, category, lastModified, [budgetId+date]',
      syncQueue: '++id, entityType, entityId, changeType, createdAt',
      conflicts: '++id, entityType, entityId, resolvedAt',
      metadata: 'key',
    });

    // Index attempts so pending-queue queries can filter retries.
    this.version(2).stores({
      syncQueue: '++id, entityType, entityId, changeType, createdAt, attempts',
    });

    // Face ID / WebAuthn device unlock + vault metadata (wrapped DEK lives here).
    this.version(3).stores({
      deviceUnlock: 'id, userId, credentialId',
    });
  }
}

/** Local enrollment record for PWA Face ID unlock (one row, id = 'local'). */
export interface DeviceUnlockRecord {
  id: string;
  userId: string;
  /** WebAuthn credential id (base64url). */
  credentialId: string;
  /** Server-side credential row id when known. */
  serverCredentialId?: string;
  enrolledAt: string;
  /** True when PRF wrap of DEK succeeded at enroll time. */
  prfEnabled: boolean;
  /** base64url(iv||ciphertext) of AES-GCM wrapped DEK; only when prfEnabled. */
  wrappedDek?: string;
  authenticatorLabel: string;
}

export interface QueuedChange {
  id?: number;
  entityType: 'BUDGET' | 'TRANSACTION';
  entityId: string;
  changeType: 'CREATE' | 'UPDATE' | 'DELETE';
  data: string;
  version: number;
  createdAt: string;
  attempts: number;
}

export interface StoredConflict {
  id?: number;
  entityType: string;
  entityId: string;
  clientData: string;
  serverData: string;
  resolution: string;
  message: string;
  createdAt: string;
  resolvedAt: string | null;
}

export interface Metadata {
  key: string;
  value: string;
}

// Singleton instance
export const db = new BalanceDatabase();

async function sealed() {
  const { isSealedMode, sealedBudgetStore, sealedTransactionStore, sealedSyncQueueStore } =
    await import('@/lib/sealedOffline');
  return {
    active: isSealedMode(),
    budgets: sealedBudgetStore,
    transactions: sealedTransactionStore,
    syncQueue: sealedSyncQueueStore,
  };
}

/**
 * Database operations for budgets.
 * When PRF vault DEK is unlocked, reads/writes go to encrypted sealed storage.
 */
export const budgetDb = {
  async getAll(ownerId: string): Promise<Budget[]> {
    const s = await sealed();
    if (s.active) return s.budgets.getAll(ownerId);
    // Local DB only holds budgets accessible to this account (owned + shared).
    // Do not filter solely by ownerId — shared budgets keep the owner's id.
    const all = await db.budgets.toArray();
    return all.filter((b) => b.ownerId === ownerId || b.owned === false);
  },

  async getById(id: string): Promise<Budget | undefined> {
    const s = await sealed();
    if (s.active) return s.budgets.getById(id);
    return db.budgets.get(id);
  },

  async getActive(ownerId: string): Promise<Budget[]> {
    const s = await sealed();
    if (s.active) return s.budgets.getActive(ownerId);
    const today = new Date().toISOString().split('T')[0];
    const all = await this.getAll(ownerId);
    return all.filter((b) => b.startDate <= today && b.endDate >= today);
  },

  async getByParent(parentId: string): Promise<Budget[]> {
    const s = await sealed();
    if (s.active) return s.budgets.getByParent(parentId);
    return db.budgets.where('parentId').equals(parentId).toArray();
  },

  async save(budget: Budget): Promise<void> {
    const s = await sealed();
    if (s.active) return s.budgets.save(budget);
    await db.budgets.put(budget);
  },

  async saveMany(budgets: Budget[]): Promise<void> {
    const s = await sealed();
    if (s.active) return s.budgets.saveMany(budgets);
    await db.budgets.bulkPut(budgets);
  },

  async delete(id: string): Promise<void> {
    const s = await sealed();
    if (s.active) return s.budgets.delete(id);
    await db.budgets.delete(id);
  },

  async clear(): Promise<void> {
    const s = await sealed();
    if (s.active) return s.budgets.clear();
    await db.budgets.clear();
  },
};

/**
 * Database operations for transactions.
 */
export const transactionDb = {
  async getByBudget(budgetId: string): Promise<Transaction[]> {
    const s = await sealed();
    if (s.active) return s.transactions.getByBudget(budgetId);
    return db.transactions.where('budgetId').equals(budgetId).reverse().sortBy('date');
  },

  async getById(id: string): Promise<Transaction | undefined> {
    const s = await sealed();
    if (s.active) return s.transactions.getById(id);
    return db.transactions.get(id);
  },

  async getByDateRange(budgetId: string, startDate: string, endDate: string): Promise<Transaction[]> {
    const s = await sealed();
    if (s.active) return s.transactions.getByDateRange(budgetId, startDate, endDate);
    return db.transactions
      .where('[budgetId+date]')
      .between([budgetId, startDate], [budgetId, endDate], true, true)
      .toArray();
  },

  async save(transaction: Transaction): Promise<void> {
    const s = await sealed();
    if (s.active) return s.transactions.save(transaction);
    await db.transactions.put(transaction);
  },

  async saveMany(transactions: Transaction[]): Promise<void> {
    const s = await sealed();
    if (s.active) return s.transactions.saveMany(transactions);
    await db.transactions.bulkPut(transactions);
  },

  async delete(id: string): Promise<void> {
    const s = await sealed();
    if (s.active) return s.transactions.delete(id);
    await db.transactions.delete(id);
  },

  async clear(): Promise<void> {
    const s = await sealed();
    if (s.active) return s.transactions.clear();
    await db.transactions.clear();
  },
};

/**
 * Sync queue operations.
 */
export const syncQueueDb = {
  async add(change: Omit<QueuedChange, 'id'>): Promise<number> {
    const s = await sealed();
    if (s.active) return s.syncQueue.add(change);
    return db.syncQueue.add(change as QueuedChange);
  },

  async getAll(): Promise<QueuedChange[]> {
    const s = await sealed();
    if (s.active) return s.syncQueue.getAll();
    return db.syncQueue.orderBy('createdAt').toArray();
  },

  async getPending(): Promise<QueuedChange[]> {
    const s = await sealed();
    if (s.active) return s.syncQueue.getPending();
    return db.syncQueue.where('attempts').below(3).toArray();
  },

  async incrementAttempts(id: number): Promise<void> {
    const s = await sealed();
    if (s.active) return s.syncQueue.incrementAttempts(id);
    const row = await db.syncQueue.get(id);
    await db.syncQueue.update(id, { attempts: (row?.attempts ?? 0) + 1 });
  },

  async remove(id: number): Promise<void> {
    const s = await sealed();
    if (s.active) return s.syncQueue.remove(id);
    await db.syncQueue.delete(id);
  },

  async clear(): Promise<void> {
    const s = await sealed();
    if (s.active) return s.syncQueue.clear();
    await db.syncQueue.clear();
  },

  async count(): Promise<number> {
    const s = await sealed();
    if (s.active) return s.syncQueue.count();
    return db.syncQueue.count();
  },
};

/**
 * Metadata operations.
 */
export const metadataDb = {
  async get(key: string): Promise<string | null> {
    const record = await db.metadata.get(key);
    return record?.value ?? null;
  },

  async set(key: string, value: string): Promise<void> {
    await db.metadata.put({ key, value });
  },

  async getLastSyncTime(): Promise<string | null> {
    return this.get('lastSyncTime');
  },

  async setLastSyncTime(time: string): Promise<void> {
    await this.set('lastSyncTime', time);
  },

  async getDeviceId(): Promise<string> {
    let deviceId = await this.get('deviceId');
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      await this.set('deviceId', deviceId);
    }
    return deviceId;
  },
};

/**
 * Conflict storage operations.
 */
export const conflictDb = {
  async add(conflict: SyncConflict): Promise<number> {
    return db.conflicts.add({
      entityType: conflict.entityType,
      entityId: conflict.entityId,
      clientData: conflict.clientData,
      serverData: conflict.serverData,
      resolution: conflict.resolution,
      message: conflict.message,
      createdAt: new Date().toISOString(),
      resolvedAt: null,
    });
  },

  async getUnresolved(): Promise<StoredConflict[]> {
    return db.conflicts
      .filter(c => c.resolvedAt === null)
      .toArray();
  },

  async resolve(id: number): Promise<void> {
    await db.conflicts.update(id, { resolvedAt: new Date().toISOString() });
  },

  async count(): Promise<number> {
    return db.conflicts.filter(c => c.resolvedAt === null).count();
  },
};

export const LOCAL_DEVICE_UNLOCK_ID = 'local';

export const deviceUnlockDb = {
  async get(): Promise<DeviceUnlockRecord | undefined> {
    return db.deviceUnlock.get(LOCAL_DEVICE_UNLOCK_ID);
  },

  async save(record: Omit<DeviceUnlockRecord, 'id'> & { id?: string }): Promise<void> {
    await db.deviceUnlock.put({ ...record, id: LOCAL_DEVICE_UNLOCK_ID });
  },

  async clear(): Promise<void> {
    await db.deviceUnlock.clear();
  },

  async isEnrolled(): Promise<boolean> {
    const row = await this.get();
    return Boolean(row?.credentialId);
  },
};

/** Wipe all local offline data (e.g. on account deletion). */
export async function clearAllLocalData(): Promise<void> {
  const { wipeSealedCache, destroySealedVaultStorage } = await import('@/lib/sealedOffline');
  wipeSealedCache();
  await destroySealedVaultStorage();
  await Promise.all([
    db.budgets.clear(),
    db.transactions.clear(),
    db.syncQueue.clear(),
    db.conflicts.clear(),
    deviceUnlockDb.clear(),
  ]);
  await db.metadata.clear();
}

/**
 * Wipe Face ID vault + local offline data without syncing queued changes.
 * Used after remote revoke or deleted account.
 */
export async function wipeLocalVaultAndData(): Promise<void> {
  await clearAllLocalData();
}
